/**
 * Client-side video trimming.
 *
 * Produces a new MP4 Blob containing only the [start, end] region of a source
 * video, entirely in the browser. It reuses the TimelineEditor WebCodecs
 * encoder + mp4-muxer pipeline:
 *   - Video frames are captured by seeking an offscreen <video> to each frame
 *     time and encoding the displayed frame (codec-agnostic, no manual demux).
 *   - Audio is decoded via the WebAudio API, the selected region is sliced out,
 *     and re-encoded to AAC.
 * The two encoded streams are then muxed into a single fast-start MP4.
 */

import { isEncodingSupported, VideoEncoderWrapper, AudioEncoderWrapper } from '@/components/TimelineEditor/export/encoder'
import { createMuxer } from '@/components/TimelineEditor/export/muxer'

const AUDIO_FRAME_SIZE = 1024 // AAC frame size

// Whether the current browser can perform a client-side trim.
export function isTrimSupported() {
    return isEncodingSupported() && typeof VideoFrame !== 'undefined'
}

// Seek the video element to `time` (seconds) and resolve once the frame is ready.
function seekTo(video, time) {
    return new Promise((resolve, reject) => {
        const cleanup = () => {
            video.removeEventListener('seeked', onSeeked)
            video.removeEventListener('error', onError)
        }
        const onSeeked = () => { cleanup(); resolve() }
        const onError = () => { cleanup(); reject(new Error('Video seek failed')) }
        video.addEventListener('seeked', onSeeked)
        video.addEventListener('error', onError)
        // Clamp to a hair below duration to avoid seeks that never fire `seeked`.
        video.currentTime = Math.min(time, Math.max(0, (video.duration || time) - 1e-3))
    })
}

// Create an offscreen <video> bound to `src` and resolve once metadata is known.
function createSourceVideo(src, crossOrigin) {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video')
        if (crossOrigin) video.crossOrigin = crossOrigin
        video.preload = 'auto'
        video.muted = true
        video.playsInline = true
        video.src = src
        const cleanup = () => {
            video.removeEventListener('loadedmetadata', onLoaded)
            video.removeEventListener('error', onError)
        }
        const onLoaded = () => { cleanup(); resolve(video) }
        const onError = () => { cleanup(); reject(new Error('Failed to load source video')) }
        video.addEventListener('loadedmetadata', onLoaded)
        video.addEventListener('error', onError)
        video.load()
    })
}

// Decode the [start, end] audio region into planar Float32 channel buffers.
// Returns null when the source has no decodable audio.
async function decodeAudioRegion(src, start, end, sampleRate) {
    try {
        const response = await fetch(src, { mode: 'cors' })
        if (!response.ok) return null
        const arrayBuffer = await response.arrayBuffer()
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        if (!AudioCtx) return null
        const audioContext = new AudioCtx({ sampleRate })
        let audioBuffer
        try {
            audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
        } finally {
            audioContext.close()
        }
        if (!audioBuffer || audioBuffer.numberOfChannels === 0) return null

        const channels = Math.min(2, audioBuffer.numberOfChannels)
        const rate = audioBuffer.sampleRate
        const startSample = Math.max(0, Math.floor(start * rate))
        const endSample = Math.min(audioBuffer.length, Math.ceil(end * rate))
        const length = Math.max(0, endSample - startSample)
        if (length === 0) return null

        const data = []
        for (let ch = 0; ch < channels; ch++) {
            const full = audioBuffer.getChannelData(ch)
            data.push(full.slice(startSample, endSample))
        }
        return { data, sampleRate: rate, channels, length }
    } catch (err) {
        console.warn('Audio decode for trim failed; producing video-only clip:', err)
        return null
    }
}

/**
 * Trim a video to the [start, end] range and return an MP4 Blob.
 *
 * @param {Object} options
 * @param {string} options.src - Source video URL.
 * @param {number} options.start - Start time in seconds.
 * @param {number} options.end - End time in seconds.
 * @param {string} [options.crossOrigin='anonymous'] - crossOrigin for the offscreen video.
 * @param {number} [options.fps=30] - Output frame rate.
 * @param {number} [options.videoBitrate] - Video bitrate in bps (defaults by resolution).
 * @param {number} [options.audioBitrate=128000] - Audio bitrate in bps.
 * @param {(progress:number)=>void} [options.onProgress] - Progress callback (0-1).
 * @param {AbortSignal} [options.signal] - Cancellation signal.
 * @returns {Promise<Blob>} The trimmed MP4.
 */
export async function trimVideo({
    src,
    start,
    end,
    crossOrigin = 'anonymous',
    fps = 30,
    videoBitrate,
    audioBitrate = 128_000,
    sampleRate = 48_000,
    onProgress = () => {},
    signal,
}) {
    if (!isTrimSupported()) {
        throw new Error('Client-side trimming is not supported in this browser')
    }
    if (!src) throw new Error('No video source provided')

    const throwIfAborted = () => {
        if (signal?.aborted) throw new DOMException('Trim cancelled', 'AbortError')
    }

    const video = await createSourceVideo(src, crossOrigin)

    try {
        const sourceDuration = video.duration || end
        const clampedStart = Math.max(0, Math.min(start ?? 0, sourceDuration))
        const clampedEnd = Math.max(clampedStart, Math.min(end ?? sourceDuration, sourceDuration))
        const trimDuration = clampedEnd - clampedStart
        if (trimDuration <= 0) throw new Error('Trim range is empty')

        // H.264 requires even dimensions.
        const width = (video.videoWidth || 1280) & ~1
        const height = (video.videoHeight || 720) & ~1

        const bitrate = videoBitrate || Math.max(2_000_000, Math.round(width * height * fps * 0.1))

        const videoChunks = []
        const audioChunks = []

        const videoEncoder = new VideoEncoderWrapper({
            width,
            height,
            fps,
            bitrate,
            onChunk: (chunk, meta) => videoChunks.push({ chunk, meta }),
        })

        // Decode audio region up-front so we know whether to configure an audio track.
        onProgress(0)
        const audio = await decodeAudioRegion(src, clampedStart, clampedEnd, sampleRate)
        throwIfAborted()

        let audioEncoder = null
        if (audio) {
            audioEncoder = new AudioEncoderWrapper({
                sampleRate: audio.sampleRate,
                channels: audio.channels,
                bitrate: audioBitrate,
                onChunk: (chunk, meta) => audioChunks.push({ chunk, meta }),
            })
        }

        try {
            await videoEncoder.init()
            if (audioEncoder) await audioEncoder.init()

            // === VIDEO: seek-and-capture each frame ===
            // Sample at frame CENTERS (+0.5 frame), never at boundaries. Seeking
            // to an exact frame boundary is ambiguous: the decoder returns the
            // frame whose PTS is <= the requested time, and floating-point error
            // routinely resolves that to the PREVIOUS frame. That is what makes a
            // trim placed on a scene cut include the last frame of the prior
            // scene. `startFrame` is derived with floor() so we begin on exactly
            // the frame that was displayed at the in-point.
            const startFrame = Math.floor(clampedStart * fps + 1e-6)
            const totalFrames = Math.max(1, Math.round(trimDuration * fps))
            const keyFrameInterval = Math.max(1, Math.round(fps * 2))

            for (let frameIndex = 0; frameIndex < totalFrames; frameIndex++) {
                throwIfAborted()
                const frameTime = (startFrame + frameIndex + 0.5) / fps
                await seekTo(video, frameTime)

                const timestamp = Math.round((frameIndex / fps) * 1_000_000)
                const frame = new VideoFrame(video, {
                    timestamp,
                    duration: Math.round(1_000_000 / fps),
                })
                videoEncoder.encode(frame, { keyFrame: frameIndex % keyFrameInterval === 0 })
                frame.close()

                // Reserve the last 10% for audio + muxing.
                onProgress(((frameIndex + 1) / totalFrames) * 0.85)

                if (frameIndex % 5 === 0) {
                    await new Promise((r) => setTimeout(r, 0))
                }
            }

            // === AUDIO: re-encode the sliced region ===
            if (audio && audioEncoder) {
                const { data, sampleRate: rate, channels, length } = audio
                let offset = 0
                while (offset < length) {
                    throwIfAborted()
                    const numFrames = Math.min(AUDIO_FRAME_SIZE, length - offset)
                    // f32-planar layout: channel buffers concatenated end-to-end.
                    const planar = new Float32Array(numFrames * channels)
                    for (let ch = 0; ch < channels; ch++) {
                        planar.set(data[ch].subarray(offset, offset + numFrames), ch * numFrames)
                    }
                    const audioData = new AudioData({
                        format: 'f32-planar',
                        sampleRate: rate,
                        numberOfChannels: channels,
                        numberOfFrames: numFrames,
                        timestamp: Math.round((offset / rate) * 1_000_000),
                        data: planar,
                    })
                    audioEncoder.encode(audioData)
                    audioData.close()
                    offset += numFrames
                }
            }

            onProgress(0.9)

            await videoEncoder.flush()
            if (audioEncoder) await audioEncoder.flush()

            // === MUX ===
            const hasAudio = audioChunks.length > 0
            const muxer = createMuxer({
                width,
                height,
                fps,
                sampleRate: audio?.sampleRate || sampleRate,
                channels: audio?.channels || 2,
                videoCodec: videoEncoder.codec,
                audioCodec: hasAudio ? audioEncoder.codec : null,
            })

            for (const { chunk, meta } of videoChunks) {
                muxer.addVideoChunk(chunk, meta)
            }
            if (hasAudio) {
                for (const { chunk, meta } of audioChunks) {
                    muxer.addAudioChunk(chunk, meta)
                }
            }

            const blob = muxer.finalize()
            onProgress(1)
            return blob
        } finally {
            videoEncoder.close()
            audioEncoder?.close()
        }
    } finally {
        video.removeAttribute('src')
        video.load()
    }
}
